/**
 * The Quotation doctype's mandatory header — and how the app copes with it (2026-09-17).
 *
 * Measured against the doctype that day: `company`, `vessel`, `port`, `delivery_location`,
 * `sales_person`, `purchaser`, `customer_reference_no`, `customer_currency` and — from the
 * afternoon — `enquiry_type` (a Link to the Enquiry Type master) are all
 * `reqd: 1` (with `order_type`, which defaults to "Sales"). Frappe validates them on EVERY save,
 * so a quotation created before the change, missing any of them, now refuses every write —
 * including a line price or a derived figure — with
 *
 *     MandatoryError: [Quotation, QTN-2026-00058]: vessel, port, sales_person, customer_reference_no
 *
 * and the header card could not dig it out either: setting the vessel alone failed for the
 * three others. Two answers:
 *
 *   creation   `missingQuotationMandatory` checks the body BEFORE `create_quotation`, so the
 *              purchaser reads "set vessel and port on the enquiry first" instead of a 417;
 *   updates    a write that fails with MandatoryError is retried ONCE with the named fields
 *              filled from the enquiry (`fillFromEnquiry`) — the enquiry is where they live, and
 *              the quotation copies them at creation anyway. What the enquiry lacks too is
 *              reported by name, as the thing to fix on the enquiry header.
 */

export const QUOTATION_MANDATORY = ['company', 'customer', 'vessel', 'port', 'delivery_location', 'sales_person', 'purchaser', 'customer_reference_no', 'customer_currency', 'enquiry_type'] as const
export type QuotationMandatoryField = (typeof QUOTATION_MANDATORY)[number]

const blank = (v: unknown) => v === undefined || v === null || String(v).trim() === ''

export function missingQuotationMandatory(body: Partial<Record<QuotationMandatoryField, unknown>>): QuotationMandatoryField[] {
  return QUOTATION_MANDATORY.filter((k) => blank(body[k]))
}

/** The field names a MandatoryError names — `[]` for any other error. */
export function mandatoryFieldsFrom(message: string): string[] {
  const listed = /MandatoryError:?\s*\[[^\]]*\]:\s*([^\n"]+)/.exec(message)
  if (listed) {
    return listed[1]
      .split(',')
      .map((s) => s.trim())
      .filter((s) => /^[a-z_]+$/.test(s))
  }
  // The other phrasing Frappe uses (`_("Value missing for {0}: {1}")`) — the field's LABEL, one
  // at a time, which is why the caller loops.
  const labelled = /Value missing for Quotation:\s*([^\n"]+)/.exec(message)
  if (labelled) {
    const field = FIELD_BY_LABEL[labelled[1].trim().toLowerCase()]
    return field ? [field] : []
  }
  // ERPNext's defaults hook, before the mandatory check even runs: "Company is required to fetch
  // default warehouse" (2026-09-17). Same cure — the label names the field.
  const required = /([A-Z][A-Za-z ]+?) is required\b/.exec(message)
  if (required) {
    const field = FIELD_BY_LABEL[required[1].trim().toLowerCase()]
    return field ? [field] : []
  }
  return []
}

const FIELD_BY_LABEL: Record<string, QuotationMandatoryField> = {
  company: 'company',
  customer: 'customer',
  vessel: 'vessel',
  port: 'port',
  'delivery location': 'delivery_location',
  'sales person': 'sales_person',
  purchaser: 'purchaser',
  'customer ref no': 'customer_reference_no',
  'customer reference no': 'customer_reference_no',
  'customer currency': 'customer_currency',
  'enquiry type': 'enquiry_type',
}

/** An enquiry as `get_enquiry` returns it — only the fields a quotation copies. */
export interface EnquiryHeaderSource {
  company?: string | null
  customer?: string | null
  vessel?: string | null
  port_of_delivery?: string | null
  delivery_location?: string | null
  sales_person?: string | null
  purchaser?: string | null
  customer_reference_no?: string | null
  customer_currency?: string | null
  enquiry_type?: string | null
}

const SOURCE: Record<string, keyof EnquiryHeaderSource> = {
  company: 'company',
  customer: 'customer',
  vessel: 'vessel',
  port: 'port_of_delivery',
  delivery_location: 'delivery_location',
  sales_person: 'sales_person',
  purchaser: 'purchaser',
  customer_reference_no: 'customer_reference_no',
  customer_currency: 'customer_currency',
  enquiry_type: 'enquiry_type',
}

export function fillFromEnquiry(fields: readonly string[], enquiry: EnquiryHeaderSource): { filled: Record<string, string>; stillMissing: string[] } {
  const filled: Record<string, string> = {}
  const stillMissing: string[] = []
  for (const field of fields) {
    const key = SOURCE[field]
    const value = key ? enquiry[key] : undefined
    if (!blank(value)) filled[field] = String(value).trim()
    else stillMissing.push(field)
  }
  return { filled, stillMissing }
}

/** "vessel, port and customer reference" — for a sentence. */
export function describeMandatory(fields: readonly string[]): string {
  const label: Record<string, string> = {
    company: 'company', customer: 'customer', vessel: 'vessel', port: 'port', delivery_location: 'delivery location',
    sales_person: 'sales person', purchaser: 'purchaser', customer_reference_no: 'customer reference no', customer_currency: 'customer currency', enquiry_type: 'enquiry type',
  }
  const names = fields.map((f) => label[f] ?? f)
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * The quotation's mandatory header fields the ENQUIRY must supply — checked before an RFQ is
 * sent (Akhil, 2026-09-17), so that by the time the comparison builds the quotation nothing is
 * missing. `customer_currency` is not here: the quotation's is chosen at build time.
 */
export function enquiryHeaderGaps(header: {
  company?: string | null
  customer?: string | null
  vessel?: string | null
  delivery_port?: string | null
  delivery_location?: string | null
  sales_person?: string | null
  purchaser?: string | null
  customer_reference_no?: string | null
  enquiry_type?: string | null
}): QuotationMandatoryField[] {
  const gaps: QuotationMandatoryField[] = []
  if (blank(header.enquiry_type)) gaps.push('enquiry_type')
  if (blank(header.company)) gaps.push('company')
  if (blank(header.customer)) gaps.push('customer')
  if (blank(header.vessel)) gaps.push('vessel')
  if (blank(header.delivery_port)) gaps.push('port')
  if (blank(header.delivery_location)) gaps.push('delivery_location')
  if (blank(header.sales_person)) gaps.push('sales_person')
  if (blank(header.purchaser)) gaps.push('purchaser')
  if (blank(header.customer_reference_no)) gaps.push('customer_reference_no')
  return gaps
}
